/**
 * Layout diagnostics — computes rich per-node / per-edge / per-pair data
 * about the current layout state for the debug view. Mirrors the solver's
 * force function so the numbers shown match what the solver is actually
 * reacting to.
 *
 * The values are read-only snapshots of the layout in its current state,
 * not the final equilibrium — so a node with a big force vector "wants to
 * move" in that direction, and an edge with high stretch is fighting the
 * solver.
 */
import type { BoardSpace, BoardItem, BoardEdge } from "./types.js";
import type { Module } from "../arch-model/model.js";
import { getTeam, getDomain } from "../arch-model/utils.js";
import {
  IDEAL_BY_MIN_DEGREE,
  IDEAL_CAP,
  IDEAL_TEAM_BONUS,
  W_CONN,
  W_FLOW,
  W_ALIGN,
  W_PARENT,
  W_PORT,
  W_REPULSE_STRONG,
  REPULSE_STRONG_CUTOFF,
  REPULSE_STRONG_FLOOR,
  W_REPULSE_WEAK,
  REPULSE_WEAK_CUTOFF,
  REPULSE_WEAK_FLOOR,
} from "./layoutParams.js";

export interface NodeDiag {
  readonly id: string;
  readonly label: string;
  readonly cx: number;
  readonly cy: number;
  readonly halfW: number;
  readonly halfH: number;
  readonly degree: number;
  readonly inDegree: number;
  readonly outDegree: number;
  readonly team?: string;
  readonly domain?: string;
  /** Net gradient — opposite of the direction the solver would move this node. */
  readonly netForce: { x: number; y: number };
  readonly netForceMag: number;
  /** Weighted average of where neighbours want this node to be. */
  readonly idealCx: number;
  readonly idealCy: number;
  readonly distFromIdeal: number;
  /** Closest neighbour (any edge status) and its centre distance. */
  readonly closestDist: number;
  readonly closestId: string;
}

export interface EdgeDiag {
  readonly id: string;
  readonly fromId: string;
  readonly toId: string;
  /** Centre-to-centre pixel distance (what the renderer draws). */
  readonly centerDist: number;
  /** Ellipse edge-to-edge gap (what the solver uses). */
  readonly edgeGap: number;
  readonly idealLen: number;
  /** (edgeGap − idealLen) / idealLen in percent. */
  readonly stretchPct: number;
  readonly stiffness: number;
  /** Scalar spring force magnitude; positive = pulling together. */
  readonly springForce: number;
  /** True if flow bias is being violated (target left of source). */
  readonly flowViolation: boolean;
  /** Degrees of freedom axis-alignment bonus is pushing on. */
  readonly dominantAxis: "H" | "V";
  readonly minDegree: number;
  readonly sameTeam: boolean;
}

export interface PairDiag {
  readonly idA: string;
  readonly idB: string;
  readonly edgeGap: number;
  readonly repulsion: number;
}

export interface SpaceDiag {
  readonly spaceId: string;
  readonly items: readonly NodeDiag[];
  readonly edges: readonly EdgeDiag[];
  /** Only close-range unconnected pairs — the ones currently repelling. */
  readonly hotPairs: readonly PairDiag[];
  readonly summary: {
    readonly nodeCount: number;
    readonly edgeCount: number;
    readonly avgStretchPct: number;
    readonly maxStretchPct: number;
    readonly flowViolations: number;
    readonly overlappingPairs: number;
  };
}

/**
 * Radius of an ellipse along a unit direction — smooth equivalent of the
 * rectangle half-width along the chord. Duplicated from stressLayout.ts
 * to keep this module import-independent.
 */
function ellipseR(ux: number, uy: number, a: number, b: number): number {
  return 1 / Math.hypot(ux / a, uy / b);
}

const NODE_CORNER_R = 14;
function roundedRectR(ux: number, uy: number, a: number, b: number): number {
  const axs = Math.abs(ux);
  const ays = Math.abs(uy);
  const r = Math.min(NODE_CORNER_R, Math.min(a, b) * 0.6);
  const tSide = Math.min(
    axs > 1e-9 ? a / axs : Infinity,
    ays > 1e-9 ? b / ays : Infinity,
  );
  const hitX = tSide * axs;
  const hitY = tSide * ays;
  if (hitX <= a - r || hitY <= b - r) return tSide;
  const cx = a - r;
  const cy = b - r;
  const B = axs * cx + ays * cy;
  const C = cx * cx + cy * cy - r * r;
  const disc = B * B - C;
  if (disc < 0) return tSide;
  return B + Math.sqrt(disc);
}

function idealLenFor(
  minDeg: number,
  sameTeam: boolean,
): number {
  const idx = Math.min(minDeg, IDEAL_BY_MIN_DEGREE.length - 1);
  const base = IDEAL_BY_MIN_DEGREE[idx] ?? IDEAL_CAP;
  return sameTeam ? Math.max(0, base - IDEAL_TEAM_BONUS) : base;
}

/** Diagnostics for a single space (one layer of children). */
export function diagnoseSpace(space: BoardSpace): SpaceDiag {
  const items = space.items;
  const edges = space.edges ?? [];

  // Degree counts
  const inDeg = new Map<string, number>();
  const outDeg = new Map<string, number>();
  for (const it of items) {
    inDeg.set(it.id, 0);
    outDeg.set(it.id, 0);
  }
  for (const e of edges) {
    outDeg.set(e.from, (outDeg.get(e.from) ?? 0) + 1);
    inDeg.set(e.to, (inDeg.get(e.to) ?? 0) + 1);
  }

  // Quick lookup map, connected-set, sibling-set.
  const byId = new Map<string, BoardItem>();
  for (const it of items) byId.set(it.id, it);
  const connected = new Set<string>();
  const peersOf = new Map<string, Set<string>>();
  for (const e of edges) {
    connected.add(`${e.from}|${e.to}`);
    connected.add(`${e.to}|${e.from}`);
    if (!peersOf.has(e.from)) peersOf.set(e.from, new Set());
    if (!peersOf.has(e.to)) peersOf.set(e.to, new Set());
    peersOf.get(e.from)!.add(e.to);
    peersOf.get(e.to)!.add(e.from);
  }
  const siblings = new Set<string>();
  for (const [, peers] of peersOf) {
    const arr = [...peers];
    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        siblings.add(`${arr[i]}|${arr[j]}`);
        siblings.add(`${arr[j]}|${arr[i]}`);
      }
    }
  }

  // Edge diagnostics and parallel spring-force contributions per node.
  const netForce = new Map<string, { x: number; y: number }>();
  const idealAccum = new Map<
    string,
    { sx: number; sy: number; sw: number; count: number }
  >();
  for (const it of items) {
    netForce.set(it.id, { x: 0, y: 0 });
    idealAccum.set(it.id, { sx: 0, sy: 0, sw: 0, count: 0 });
  }

  const edgeDiags: EdgeDiag[] = [];
  let maxStretch = 0;
  let sumStretch = 0;
  let flowViolations = 0;
  let overlapping = 0;

  for (const e of edges) {
    const from = byId.get(e.from);
    const to = byId.get(e.to);
    if (!from || !to) continue;
    const fromDeg = (inDeg.get(from.id) ?? 0) + (outDeg.get(from.id) ?? 0);
    const toDeg = (inDeg.get(to.id) ?? 0) + (outDeg.get(to.id) ?? 0);
    const minDeg = Math.min(fromDeg, toDeg);

    const fromMeta = (from.meta as Module | undefined) ?? undefined;
    const toMeta = (to.meta as Module | undefined) ?? undefined;
    const fromTeam = fromMeta ? getTeam(fromMeta) : undefined;
    const toTeam = toMeta ? getTeam(toMeta) : undefined;
    const sameTeam = !!fromTeam && fromTeam === toTeam;
    const idealLen = idealLenFor(minDeg, sameTeam);
    const stiffness =
      W_CONN / Math.sqrt(Math.max(1, fromDeg) * Math.max(1, toDeg));

    const fcx = from.x + from.w / 2;
    const fcy = from.y + from.h / 2;
    const tcx = to.x + to.w / 2;
    const tcy = to.y + to.h / 2;
    const dx = tcx - fcx;
    const dy = tcy - fcy;
    const centerDist = Math.hypot(dx, dy);
    const ux = centerDist > 0 ? dx / centerDist : 1;
    const uy = centerDist > 0 ? dy / centerDist : 0;
    const rA = ellipseR(ux, uy, from.w / 2, from.h / 2);
    const rB = ellipseR(ux, uy, to.w / 2, to.h / 2);
    const edgeGap = centerDist - rA - rB;
    const stretch = edgeGap - idealLen;
    const stretchPct = (stretch / Math.max(1, idealLen)) * 100;
    const springForce = stiffness * stretch;

    // Contribute to node net force (spring term only — repulsion added below).
    const fx = springForce * ux;
    const fy = springForce * uy;
    const nf = netForce.get(from.id)!;
    const nt = netForce.get(to.id)!;
    nf.x += fx;
    nf.y += fy;
    nt.x -= fx;
    nt.y -= fy;

    // Axis-alignment bias — stored as FORCE (direction each node moves).
    // Pulls both endpoints toward the smaller-component's zero.
    if (Math.abs(dx) > Math.abs(dy)) {
      const af = W_ALIGN * dy;
      nf.y += af; // from moves toward to's y
      nt.y -= af; // to moves toward from's y
    } else {
      const af = W_ALIGN * dx;
      nf.x += af;
      nt.x -= af;
    }

    // Flow bias — source feels leftward force, target rightward.
    nf.x -= W_FLOW;
    nt.x += W_FLOW;

    // Ideal-position accumulation.
    const faFrom = idealAccum.get(from.id)!;
    faFrom.sx += stiffness * (tcx - idealLen);
    faFrom.sy += stiffness * tcy;
    faFrom.sw += stiffness;
    faFrom.count++;
    const faTo = idealAccum.get(to.id)!;
    faTo.sx += stiffness * (fcx + idealLen);
    faTo.sy += stiffness * fcy;
    faTo.sw += stiffness;
    faTo.count++;

    const flowViolation = fcx > tcx;
    if (flowViolation) flowViolations++;
    if (Math.abs(stretchPct) > Math.abs(maxStretch)) maxStretch = stretchPct;
    sumStretch += Math.abs(stretchPct);

    edgeDiags.push({
      id: e.id,
      fromId: e.from,
      toId: e.to,
      centerDist,
      edgeGap,
      idealLen,
      stretchPct,
      stiffness,
      springForce,
      flowViolation,
      dominantAxis: Math.abs(dx) > Math.abs(dy) ? "H" : "V",
      minDegree: minDeg,
      sameTeam,
    });
  }

  // Repulsion between unconnected pairs + hotPairs collection.
  const hotPairs: PairDiag[] = [];
  let closestPerNode = new Map<string, { id: string; dist: number }>();

  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i]!;
      const b = items[j]!;
      const acx = a.x + a.w / 2;
      const acy = a.y + a.h / 2;
      const bcx = b.x + b.w / 2;
      const bcy = b.y + b.h / 2;
      const dx = acx - bcx;
      const dy = acy - bcy;
      const dist = Math.hypot(dx, dy);
      const prevA = closestPerNode.get(a.id);
      if (!prevA || dist < prevA.dist) closestPerNode.set(a.id, { id: b.id, dist });
      const prevB = closestPerNode.get(b.id);
      if (!prevB || dist < prevB.dist) closestPerNode.set(b.id, { id: a.id, dist });

      // Overlap check (inflated by how we measure elsewhere).
      if (
        Math.abs(acx - bcx) < (a.w + b.w) / 2 &&
        Math.abs(acy - bcy) < (a.h + b.h) / 2
      ) {
        overlapping++;
      }

      const ux = dist > 0 ? dx / dist : 1;
      const uy = dist > 0 ? dy / dist : 0;
      const rAe = ellipseR(ux, uy, a.w / 2, a.h / 2);
      const rBe = ellipseR(ux, uy, b.w / 2, b.h / 2);
      const rAr = roundedRectR(ux, uy, a.w / 2, a.h / 2);
      const rBr = roundedRectR(ux, uy, b.w / 2, b.h / 2);
      const gapEllipse = dist - rAe - rBe;
      const gapRect = dist - rAr - rBr;
      const key = `${a.id}|${b.id}`;

      let repulsion = 0;
      if (gapRect <= REPULSE_STRONG_CUTOFF) {
        const d = Math.max(REPULSE_STRONG_FLOOR, gapRect);
        // 1/d² with smooth cutoff — see stressLayout.ts.
        repulsion +=
          W_REPULSE_STRONG / (d * d) -
          W_REPULSE_STRONG /
            (REPULSE_STRONG_CUTOFF * REPULSE_STRONG_CUTOFF);
      }
      const unrelated = !connected.has(key) && !siblings.has(key);
      if (unrelated && gapEllipse <= REPULSE_WEAK_CUTOFF) {
        const d = Math.max(REPULSE_WEAK_FLOOR, gapEllipse);
        repulsion += W_REPULSE_WEAK / (d * d);
      }
      if (repulsion === 0) continue;

      // ux points from b to a; repulsion pushes a away from b (+ux direction).
      netForce.get(a.id)!.x += repulsion * ux;
      netForce.get(a.id)!.y += repulsion * uy;
      netForce.get(b.id)!.x -= repulsion * ux;
      netForce.get(b.id)!.y -= repulsion * uy;

      // Record meaningful pairs for the hot-pair visualisation.
      hotPairs.push({
        idA: a.id,
        idB: b.id,
        edgeGap: gapEllipse,
        repulsion,
      });
    }
  }
  // Keep only the top 40 hot pairs so the view doesn't become noise.
  hotPairs.sort((x, y) => y.repulsion - x.repulsion);
  const trimmed = hotPairs.slice(0, 40);

  // Build node diagnostics.
  const nodeDiags: NodeDiag[] = items.map((it) => {
    const nf = netForce.get(it.id)!;
    const cx = it.x + it.w / 2;
    const cy = it.y + it.h / 2;
    const fa = idealAccum.get(it.id)!;
    const idealCx = fa.sw > 0 ? fa.sx / fa.sw : cx;
    const idealCy = fa.sw > 0 ? fa.sy / fa.sw : cy;
    const closest = closestPerNode.get(it.id);
    const meta = (it.meta as Module | undefined) ?? undefined;
    return {
      id: it.id,
      label: it.label,
      cx,
      cy,
      halfW: it.w / 2,
      halfH: it.h / 2,
      degree: (inDeg.get(it.id) ?? 0) + (outDeg.get(it.id) ?? 0),
      inDegree: inDeg.get(it.id) ?? 0,
      outDegree: outDeg.get(it.id) ?? 0,
      team: meta ? getTeam(meta) : undefined,
      domain: meta ? getDomain(meta) : undefined,
      netForce: nf,
      netForceMag: Math.hypot(nf.x, nf.y),
      idealCx,
      idealCy,
      distFromIdeal: Math.hypot(idealCx - cx, idealCy - cy),
      closestDist: closest?.dist ?? Infinity,
      closestId: closest?.id ?? "",
    };
  });

  const edgeCount = edgeDiags.length;
  const avgStretchPct = edgeCount > 0 ? sumStretch / edgeCount : 0;

  return {
    spaceId: space.id,
    items: nodeDiags,
    edges: edgeDiags,
    hotPairs: trimmed,
    summary: {
      nodeCount: items.length,
      edgeCount,
      avgStretchPct,
      maxStretchPct: maxStretch,
      flowViolations,
      overlappingPairs: overlapping,
    },
  };
}

// ---- Browser console API --------------------------------------------------

/**
 * Installs a set of ad-hoc debug helpers on `window.arch` for interactive
 * querying from the devtools console. Call from App.tsx whenever `root`
 * changes. Returns a cleanup function.
 *
 * Usage in devtools:
 *   arch.nodes()                  // list all nodes across the tree
 *   arch.find("Stripe")           // fuzzy lookup → node + space refs
 *   arch.forces("Stripe")         // full force breakdown, printed as table
 *   arch.edges()                  // edge stretch table for the root space
 *   arch.diag()                   // full SpaceDiag for root
 *   arch.help()
 */
export function installWindowDebug(root: BoardSpace): () => void {
  interface Located {
    item: BoardItem;
    space: BoardSpace;
  }

  function* walk(space: BoardSpace): Generator<Located> {
    for (const item of space.items) {
      yield { item, space };
      if (item.space) yield* walk(item.space);
    }
  }

  const findNode = (query: string): Located | null => {
    if (!query) return null;
    const lower = query.toLowerCase();
    // Exact id first, then exact-label, then substring-label match.
    let exactId: Located | null = null;
    let exactLabel: Located | null = null;
    let substring: Located | null = null;
    for (const hit of walk(root)) {
      if (hit.item.id === query) {
        exactId = hit;
        break;
      }
      if (!exactLabel && hit.item.label.toLowerCase() === lower) exactLabel = hit;
      if (!substring && hit.item.label.toLowerCase().includes(lower)) substring = hit;
    }
    return exactId ?? exactLabel ?? substring;
  };

  const api = {
    root,

    help() {
      console.log(
        [
          "arch.nodes()              list all nodes",
          "arch.find('<name>')       find a node by name (fuzzy)",
          "arch.forces('<name>')     full force decomposition on a node",
          "arch.edges()              edge stretch table",
          "arch.hotPairs()           top repulsion pairs",
          "arch.diag()               full SpaceDiag for the root space",
          "arch.summary()            one-line layout summary",
          "arch.root                 raw root BoardSpace",
        ].join("\n"),
      );
    },

    nodes() {
      const rows = [...walk(root)].map(({ item, space }) => ({
        id: item.id,
        label: item.label,
        x: Math.round(item.x),
        y: Math.round(item.y),
        parent: space.id,
      }));
      console.table(rows);
      return rows;
    },

    find(query: string) {
      const hit = findNode(query);
      if (!hit) {
        console.warn(`arch.find: no node matching "${query}"`);
        return null;
      }
      console.log(`${hit.item.label} (${hit.item.id}) in space ${hit.space.id}`);
      return hit;
    },

    forces(query: string) {
      const hit = findNode(query);
      if (!hit) {
        console.warn(`arch.forces: no node matching "${query}"`);
        return null;
      }
      const breakdown = diagnoseNodeForces(hit.space, hit.item.id);
      if (!breakdown) {
        console.warn("no breakdown available");
        return null;
      }
      console.groupCollapsed(
        `%cforces on ${breakdown.label}%c  net=(${breakdown.netFx.toFixed(1)}, ${breakdown.netFy.toFixed(1)}) |F|=${breakdown.netMag.toFixed(1)}`,
        "color:#fbbf24;font-weight:bold",
        "color:#94a3b8;font-weight:normal",
      );
      // Group by type totals first.
      const totals = new Map<ForceType, number>();
      for (const f of breakdown.forces) {
        totals.set(f.type, (totals.get(f.type) ?? 0) + f.magnitude);
      }
      console.log("by type:");
      console.table(
        [...totals.entries()].map(([type, mag]) => ({
          type,
          sum: mag.toFixed(2),
        })),
      );
      console.log("individual forces (sorted by magnitude):");
      console.table(
        breakdown.forces.map((f) => ({
          type: f.type,
          source: f.peerLabel ?? "—",
          fx: f.fx.toFixed(2),
          fy: f.fy.toFixed(2),
          mag: f.magnitude.toFixed(2),
          note: f.label,
        })),
      );
      console.groupEnd();
      return breakdown;
    },

    edges() {
      const d = diagnoseSpace(root);
      const rows = d.edges.map((e) => {
        const from = root.items.find((i) => i.id === e.fromId)?.label ?? e.fromId;
        const to = root.items.find((i) => i.id === e.toId)?.label ?? e.toId;
        return {
          from,
          to,
          gap: Math.round(e.edgeGap),
          ideal: e.idealLen,
          stretch: `${e.stretchPct >= 0 ? "+" : ""}${e.stretchPct.toFixed(0)}%`,
          k: e.stiffness.toFixed(2),
          force: e.springForce.toFixed(1),
          dMin: e.minDegree,
          team: e.sameTeam ? "✓" : "",
          flow: e.flowViolation ? "⚠" : "",
        };
      });
      console.table(rows);
      return rows;
    },

    hotPairs(limit: number = 20) {
      const d = diagnoseSpace(root);
      const rows = d.hotPairs.slice(0, limit).map((p) => {
        const a = root.items.find((i) => i.id === p.idA)?.label ?? p.idA;
        const b = root.items.find((i) => i.id === p.idB)?.label ?? p.idB;
        return { a, b, gap: Math.round(p.edgeGap), force: p.repulsion.toFixed(2) };
      });
      console.table(rows);
      return rows;
    },

    diag() {
      return diagnoseSpace(root);
    },

    summary() {
      const s = diagnoseSpace(root).summary;
      console.log(
        `nodes=${s.nodeCount} edges=${s.edgeCount} · stretch avg=${s.avgStretchPct.toFixed(1)}% max=${s.maxStretchPct.toFixed(1)}% · flow_viol=${s.flowViolations} overlap=${s.overlappingPairs}`,
      );
      return s;
    },
  };

  const w = window as unknown as { arch?: typeof api };
  w.arch = api;
  if (!(window as unknown as { __archDebugGreeted?: boolean }).__archDebugGreeted) {
    console.log(
      "%carch debug%c ready — type %carch.help()%c in console",
      "background:#fbbf24;color:#0f172a;padding:2px 6px;border-radius:3px;font-weight:bold",
      "",
      "color:#38bdf8;font-family:ui-monospace,monospace",
      "",
    );
    (window as unknown as { __archDebugGreeted?: boolean }).__archDebugGreeted =
      true;
  }

  return () => {
    if (w.arch === api) delete w.arch;
  };
}

/** Recursive — diagnoses the root space and every nested space, returned flat. */
export function diagnoseTree(root: BoardSpace): SpaceDiag[] {
  const out: SpaceDiag[] = [];
  const walk = (s: BoardSpace) => {
    out.push(diagnoseSpace(s));
    for (const item of s.items) if (item.space) walk(item.space);
  };
  walk(root);
  return out;
}

// ---- Per-node force decomposition -----------------------------------------

export type ForceType =
  | "spring"
  | "repulsion"
  | "flow"
  | "alignment"
  | "port"
  | "parent";

export interface NodeForceVector {
  readonly type: ForceType;
  readonly label: string;
  /** Force vector (direction the node would move under GD = −gradient). */
  readonly fx: number;
  readonly fy: number;
  readonly magnitude: number;
  /** Optional peer centre, for drawing origin→peer guideline. */
  readonly peerCx?: number;
  readonly peerCy?: number;
  readonly peerLabel?: string;
}

export interface NodeForceBreakdown {
  readonly nodeId: string;
  readonly label: string;
  readonly cx: number;
  readonly cy: number;
  readonly halfW: number;
  readonly halfH: number;
  readonly forces: readonly NodeForceVector[];
  readonly netFx: number;
  readonly netFy: number;
  readonly netMag: number;
}

/**
 * Full force decomposition on one node — every individual contribution from
 * every spring, repulsion pair, flow edge, alignment, port, and gravity.
 * Exactly mirrors the solver's gradient, flipped in sign so magnitudes point
 * the direction the node would actually move.
 */
export function diagnoseNodeForces(
  space: BoardSpace,
  nodeId: string,
): NodeForceBreakdown | null {
  const items = space.items;
  const edges = space.edges ?? [];
  const target = items.find((i) => i.id === nodeId);
  if (!target) return null;

  const inDeg = new Map<string, number>();
  const outDeg = new Map<string, number>();
  for (const it of items) {
    inDeg.set(it.id, 0);
    outDeg.set(it.id, 0);
  }
  for (const e of edges) {
    outDeg.set(e.from, (outDeg.get(e.from) ?? 0) + 1);
    inDeg.set(e.to, (inDeg.get(e.to) ?? 0) + 1);
  }

  const byId = new Map(items.map((i) => [i.id, i] as const));
  const connected = new Set<string>();
  const peersOf = new Map<string, Set<string>>();
  for (const e of edges) {
    connected.add(`${e.from}|${e.to}`);
    connected.add(`${e.to}|${e.from}`);
    if (!peersOf.has(e.from)) peersOf.set(e.from, new Set());
    if (!peersOf.has(e.to)) peersOf.set(e.to, new Set());
    peersOf.get(e.from)!.add(e.to);
    peersOf.get(e.to)!.add(e.from);
  }
  const siblings = new Set<string>();
  for (const [, peers] of peersOf) {
    const arr = [...peers];
    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        siblings.add(`${arr[i]}|${arr[j]}`);
        siblings.add(`${arr[j]}|${arr[i]}`);
      }
    }
  }

  const cx = target.x + target.w / 2;
  const cy = target.y + target.h / 2;
  const forces: NodeForceVector[] = [];
  let netFx = 0;
  let netFy = 0;
  const push = (v: NodeForceVector) => {
    forces.push(v);
    netFx += v.fx;
    netFy += v.fy;
  };

  // --- Springs (one per connected edge) ---
  for (const e of edges) {
    if (e.from !== nodeId && e.to !== nodeId) continue;
    const peerId = e.from === nodeId ? e.to : e.from;
    const peer = byId.get(peerId);
    if (!peer) continue;
    const pcx = peer.x + peer.w / 2;
    const pcy = peer.y + peer.h / 2;
    const dx = pcx - cx;
    const dy = pcy - cy;
    const dist = Math.hypot(dx, dy) || 1e-9;
    const ux = dx / dist;
    const uy = dy / dist;
    const rSelf = ellipseR(ux, uy, target.w / 2, target.h / 2);
    const rPeer = ellipseR(ux, uy, peer.w / 2, peer.h / 2);
    const edgeGap = dist - rSelf - rPeer;

    const fromDeg = (inDeg.get(e.from) ?? 0) + (outDeg.get(e.from) ?? 0);
    const toDeg = (inDeg.get(e.to) ?? 0) + (outDeg.get(e.to) ?? 0);
    const minDeg = Math.min(fromDeg, toDeg);
    const fromMeta = (byId.get(e.from)?.meta as Module | undefined) ?? undefined;
    const toMeta = (byId.get(e.to)?.meta as Module | undefined) ?? undefined;
    const fromTeam2 = fromMeta ? getTeam(fromMeta) : undefined;
    const toTeam2 = toMeta ? getTeam(toMeta) : undefined;
    const sameTeam = !!fromTeam2 && fromTeam2 === toTeam2;
    const idealLen = idealLenFor(minDeg, sameTeam);
    const stiffness =
      W_CONN / Math.sqrt(Math.max(1, fromDeg) * Math.max(1, toDeg));

    // dE/dp_self in spring = -k*(edgeGap - idealLen)*u (pointing toward peer when stretched)
    // Wait — need to be careful. Let me derive from scratch.
    // E = 0.5 * k * (edgeGap - idealLen)²
    // edgeGap = dist - rSelf - rPeer, dist = |peer - self|
    // d(dist)/d(self) = -u (where u = (peer-self)/dist)
    // d(edgeGap)/d(self) ≈ -u (ignoring ellipse radius variation with u)
    // dE/d(self) = k*(edgeGap - idealLen)*(-u) = -k*delta*u
    // Force on self (= -gradient) = +k*delta*u (toward peer when stretched)
    const delta = edgeGap - idealLen;
    const fmag = stiffness * delta; // signed: positive = attraction toward peer
    const fx = fmag * ux;
    const fy = fmag * uy;
    push({
      type: "spring",
      label:
        (delta >= 0 ? "→ pull " : "← push ") +
        peer.label +
        (sameTeam ? " [team]" : "") +
        `  (gap ${Math.round(edgeGap)}, ideal ${idealLen})`,
      fx,
      fy,
      magnitude: Math.abs(fmag),
      peerCx: pcx,
      peerCy: pcy,
      peerLabel: peer.label,
    });
  }

  // --- Repulsion: two tiers. Strong applies even to connected pairs;
  //     weak applies only to unrelated pairs (non-connected, non-sibling).
  for (const other of items) {
    if (other.id === nodeId) continue;
    const ocx = other.x + other.w / 2;
    const ocy = other.y + other.h / 2;
    const dx = cx - ocx;
    const dy = cy - ocy;
    const dist = Math.hypot(dx, dy) || 1e-9;
    const ux = dx / dist;
    const uy = dy / dist;
    const rSelfE = ellipseR(ux, uy, target.w / 2, target.h / 2);
    const rOtherE = ellipseR(ux, uy, other.w / 2, other.h / 2);
    const rSelfR = roundedRectR(ux, uy, target.w / 2, target.h / 2);
    const rOtherR = roundedRectR(ux, uy, other.w / 2, other.h / 2);
    const gapEllipse = dist - rSelfE - rOtherE;
    const gapRect = dist - rSelfR - rOtherR;
    const gap = gapEllipse; // for the label, used below
    const key = `${nodeId}|${other.id}`;
    const isConn = connected.has(key);
    const isSib = siblings.has(key);

    let fmag = 0;
    const parts: string[] = [];
    if (gapRect <= REPULSE_STRONG_CUTOFF) {
      const d = Math.max(REPULSE_STRONG_FLOOR, gapRect);
      const strong =
        W_REPULSE_STRONG / (d * d) -
        W_REPULSE_STRONG /
          (REPULSE_STRONG_CUTOFF * REPULSE_STRONG_CUTOFF);
      fmag += strong;
      parts.push(`strong ${strong.toFixed(1)}`);
    }
    if (!isConn && !isSib && gapEllipse <= REPULSE_WEAK_CUTOFF) {
      const d = Math.max(REPULSE_WEAK_FLOOR, gapEllipse);
      const weak = W_REPULSE_WEAK / (d * d);
      fmag += weak;
      parts.push(`weak ${weak.toFixed(1)}`);
    }
    if (fmag < 0.5) continue;

    const fx = fmag * ux;
    const fy = fmag * uy;
    const relation = isConn ? "[conn]" : isSib ? "[sib]" : "";
    push({
      type: "repulsion",
      label:
        `⟵ push from ${other.label} ${relation}` +
        `  (gap ${Math.round(gap)}, ${parts.join(" + ")})`,
      fx,
      fy,
      magnitude: fmag,
      peerCx: ocx,
      peerCy: ocy,
      peerLabel: other.label,
    });
  }

  // --- Flow bias (single combined vector) ---
  // Per-edge: source gets grad.x += W_FLOW (GD moves left), target gets -W_FLOW (GD moves right).
  // Force on self = -grad → source has negative x force (left), target has positive (right).
  const outCount = outDeg.get(nodeId) ?? 0;
  const inCount = inDeg.get(nodeId) ?? 0;
  const flowFx = (inCount - outCount) * W_FLOW;
  if (Math.abs(flowFx) > 0) {
    push({
      type: "flow",
      label:
        flowFx > 0
          ? `→ flow (target of ${inCount} edge${inCount === 1 ? "" : "s"})`
          : `← flow (source of ${outCount} edge${outCount === 1 ? "" : "s"})`,
      fx: flowFx,
      fy: 0,
      magnitude: Math.abs(flowFx),
    });
  }

  // --- Alignment (per-edge axis bias, combined into a single vector) ---
  let alignFx = 0;
  let alignFy = 0;
  for (const e of edges) {
    if (e.from !== nodeId && e.to !== nodeId) continue;
    const peerId = e.from === nodeId ? e.to : e.from;
    const peer = byId.get(peerId);
    if (!peer) continue;
    const dx = (peer.x + peer.w / 2) - cx;
    const dy = (peer.y + peer.h / 2) - cy;
    if (Math.abs(dx) > Math.abs(dy)) {
      // Edge is more horizontal → gradient pulls our y toward peer's y.
      // grad_self.y -= W_ALIGN * dy → force on self = +W_ALIGN * dy
      alignFy += W_ALIGN * dy;
    } else {
      alignFx += W_ALIGN * dx;
    }
  }
  if (Math.hypot(alignFx, alignFy) > 0.1) {
    push({
      type: "alignment",
      label: "axis align",
      fx: alignFx,
      fy: alignFy,
      magnitude: Math.hypot(alignFx, alignFy),
    });
  }

  // --- External ports ---
  for (const p of target.externalPorts ?? []) {
    // "out" → push right (force +x), "in" → push left (−x).
    const fx = p.direction === "out" ? W_PORT : -W_PORT;
    push({
      type: "port",
      label: `${p.direction === "out" ? "→" : "←"} port to ${p.peerLabel}`,
      fx,
      fy: 0,
      magnitude: W_PORT,
    });
  }

  // --- Parent gravity (toward centroid) ---
  let ccx = 0;
  let ccy = 0;
  for (const it of items) {
    ccx += it.x + it.w / 2;
    ccy += it.y + it.h / 2;
  }
  ccx /= Math.max(1, items.length);
  ccy /= Math.max(1, items.length);
  // grad_self.x += W_PARENT * (cx - ccx) → force on self = -W_PARENT * (cx - ccx)
  const pgFx = -W_PARENT * (cx - ccx);
  const pgFy = -W_PARENT * (cy - ccy);
  const pgMag = Math.hypot(pgFx, pgFy);
  if (pgMag > 0.05) {
    push({
      type: "parent",
      label: "parent gravity",
      fx: pgFx,
      fy: pgFy,
      magnitude: pgMag,
    });
  }

  // Sort by magnitude so the big players render first (and their labels
  // don't get buried underneath tiny ones).
  forces.sort((a, b) => b.magnitude - a.magnitude);

  return {
    nodeId: target.id,
    label: target.label,
    cx,
    cy,
    halfW: target.w / 2,
    halfH: target.h / 2,
    forces,
    netFx,
    netFy,
    netMag: Math.hypot(netFx, netFy),
  };
}

// W_PORT and W_PARENT come from layoutParams, imported at top.

/** Color ramp for stretch — green near 0, blue when compressed, red when stretched. */
export function stretchColor(stretchPct: number): string {
  const clamped = Math.max(-100, Math.min(200, stretchPct));
  if (clamped > 0) {
    // 0 → green, 100 → red
    const t = Math.min(1, clamped / 100);
    const r = Math.round(74 + (248 - 74) * t);
    const g = Math.round(222 - (222 - 113) * t);
    const b = Math.round(128 - (128 - 113) * t);
    return `rgb(${r},${g},${b})`;
  }
  // 0 → green, -100 → blue
  const t = Math.min(1, -clamped / 100);
  const r = Math.round(74 - (74 - 56) * t);
  const g = Math.round(222 - (222 - 189) * t);
  const b = Math.round(128 + (248 - 128) * t);
  return `rgb(${r},${g},${b})`;
}
